import React, {useEffect, useState} from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import axios from 'axios';
import {Dropdown} from 'react-bootstrap';
import {formatNumberWithCommas} from '../../../../common/utils/function';
import {CONSTANT} from '../../../../common/utils/constants';

interface TokenPriceChartProps {
    stats: any;
    showPrice?: boolean;
    showMarketCap?: boolean;
    showTotalVolume?: boolean;
}

const TokenPriceChart: React.FC<TokenPriceChartProps> = ({
                                                             stats,
                                                             showPrice = false,
                                                             showMarketCap = false,
                                                             showTotalVolume = false,
                                                         }) => {
    const tokenData = showPrice ? 'Price' : showMarketCap ? 'Market Cap' : showTotalVolume ? 'Total Volume' : 'Price';
    const [selectedDateOption, setSelectedDateOption] = useState<string>('30 Days');
    const [dates, setDates] = useState<number[]>([]);
    const [priceOptions, setPriceOptions] = useState<any>({
        chart: {type: 'areaspline'},
        title: {text: `${tokenData} Chart`},
        legend: {enabled: false},
        navigator: {enabled: false},
        scrollbar: {enabled: false},
        exporting: {enabled: false},
        credits: {enabled: false},
        marker: {radius: 2},
        xAxis: {
            minorGridLineWidth: 0,
            lineColor: 'transparent',
            minorTickLength: 0,
            visible: true,
            type: 'datetime',
            title: {text: 'Date'},
            labels: {format: '{value:%d %b}'},
            lineWidth: 0,
            tickLength: 0,
        },
        yAxis: {
            opposite: false,
            labels: {autoRotation: false, padding: 0},
            minorTickLength: 0,
            tickLength: 0,
            title: {text: `${tokenData}`},
        },
        series: [],
        plotOptions: {
            series: {
                lineWidth: 2,
                fillColor: `#${CONSTANT.CHART_COLOUR}66`,
            },
        },
        tooltip: {
            enable: false,
        },
    });

    const monthlyMap: { [key: string]: number } = {
        '30 Days': 30,
        '3 Months': 90,
        '6 Months': 180,
    };

    const handleSelectDate = (option: string) => {
        setSelectedDateOption(option);
        const days = monthlyMap[option];
        setDates([Date.now() - days * 24 * 60 * 60 * 1000, Date.now()]);
    };

    const priceChart = async () => {
        try {
            const days = monthlyMap[selectedDateOption];
            const response = await axios.get(`https://api.coingecko.com/api/v3/coins/${stats.coingecko_data.id}/market_chart?vs_currency=usd&days=${days}&interval=daily`);
            const {prices, market_caps, total_volumes} = response.data;

            const series: any[] = [];
            if (showPrice) {
                series.push({
                    type: 'areaspline',
                    name: 'Price',
                    data: prices,
                    color: `#${CONSTANT.CHART_COLOUR}`,
                    marker: {
                        enabled: true,
                        radius: 3,
                    },
                });
            }
            if (showMarketCap) {
                series.push({
                    type: 'areaspline',
                    name: 'Market Cap',
                    data: market_caps,
                    color: `#${CONSTANT.CHART_COLOUR}`,
                    marker: {
                        enabled: true,
                        radius: 3,
                    },
                });
            }
            if (showTotalVolume) {
                series.push({
                    type: 'areaspline',
                    name: 'Total Volume',
                    data: total_volumes,
                    color: `#${CONSTANT.CHART_COLOUR}`,
                    marker: {
                        enabled: true,
                        radius: 3,
                    },
                });
            }

            setPriceOptions({
                ...priceOptions,
                series,
                tooltip: {
                    shared: true,
                    enabled: true,
                    useHTML: true,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderColor: `#${CONSTANT.CHART_COLOUR}`,
                    borderWidth: 1,
                    formatter: function (this: any) {
                        const points = this.points;
                        const index = points[0]?.point.index ?? 0;
                        const marketCap = showMarketCap ? market_caps[index][1] : 'N/A';
                        const totalVolume = showTotalVolume ? total_volumes[index][1] : 'N/A';
                        const price = showPrice ? points[0]?.y : 'N/A';

                        return `
                <table>
                    <tr>
                    <td style="font-size: 10px; padding-right: 10px;"><strong>Date:</strong></td>
                    <td style="font-size: 12px;">${Highcharts.dateFormat('%d %b %Y', this.x)}</td>
                    </tr>
                    ${showPrice ? `<tr><td style="font-size: 10px; padding-right: 10px;"><strong>Price:</strong></td><td>$${Highcharts.numberFormat(price, 6)}</td></tr>` : ''}
                    ${showMarketCap ? `<tr><td style="font-size: 10px; padding-right: 10px;"><strong>Market Cap:</strong></td><td>$${formatNumberWithCommas(marketCap.toString())}</td></tr>` : ''}
                    ${showTotalVolume ? `<tr><td style="font-size: 10px; padding-right: 10px;"><strong>Total Volume:</strong></td><td>${Highcharts.numberFormat(totalVolume, 2)}</td></tr>` : ''}
                </table>
                `;
                    },
                }
            });
        } catch (error) {
            console.error('Error fetching token data:', error);
        }
    };

    useEffect(() => {
        if (stats && stats.coingecko_data) {
            priceChart();
        }
    }, [selectedDateOption, stats]);

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between pb-3">
                <h2 className="bs-card-header-title">Token {tokenData}</h2>
                <Dropdown className="d-flex bs_token-social" align="end">
                    <Dropdown.Toggle id="dropdown-button-dark-example1" variant="white" size="sm">
                        {selectedDateOption}
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="dark" align="start" className="custom-menu">
                        {['30 Days', '3 Months', '6 Months'].map((option) => (
                            <Dropdown.Item
                                key={option}
                                className="bs-small-font"
                                onClick={() => handleSelectDate(option)}
                            >
                                {option}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            <div className="mb-3">
                <HighchartsReact highcharts={Highcharts} options={priceOptions}/>
            </div>
        </React.Fragment>
    );
};

export default TokenPriceChart;
